/**
 * PriceLab Database Utility
 * Thin wrapper over api.ts that handles snake_case → camelCase conversion
 * Components import from here for camelCase-typed data access
 */

import type {
  Product,
  SKUMapping,
  CategoryExpenseRates,
  ImportHistory,
  DatabaseStats,
  MarketplacePlatform,
  Marketplace,
  MarketplaceProductData,
  MarketplaceColumn,
  MarketplaceCountry,
  CategoryColumnDefaults,
} from '../types/database';
import type {
  ShippingCarrier,
  CarrierRouteRates,
  ShippingRouteId,
  RouteShippingConfig,
  CustomShippingRoute,
} from '../types/shipping';
import { DEFAULT_CARRIERS, SHIPPING_ROUTES } from '../types/shipping';

import {
  apiGetAllProducts,
  apiGetProduct,
  apiSaveProduct,
  apiUpdateProduct,
  apiDeleteProduct,
  apiBulkSaveProducts,
  apiGetProductStats,
  apiGetSKUMappingsByProduct,
  apiGetSKUMappingsByPlatform,
  apiFindSKUMapping,
  apiSaveSKUMapping,
  apiGetAllCategoryExpenses,
  apiGetCategoryExpense,
  apiGetCategoryExpensesByPlatform,
  apiUpsertCategoryExpense,
  apiGetAllMarketplaces,
  apiGetMarketplace,
  apiSaveMarketplace,
  apiUpdateMarketplace,
  apiDeleteMarketplace,
  apiGetMarketplaceProductData,
  apiGetProductDataByMarketplace,
  apiUpsertMarketplaceProductData,
  apiBulkSaveMarketplaceProductData,
  apiGetCategoryColumnDefaults,
  apiGetCategoryDefaultsByMarketplace,
  apiUpsertCategoryColumnDefaults,
  apiDeleteCategoryColumnDefaults,
  apiDeleteCategoryDefaultsByCountry,
  apiDeleteCategoryDefaultsByMarketplace,
  apiBulkUpsertCategoryColumnDefaults,
  apiGetAllCategoryColumnDefaults,
  apiGetSetting,
  apiSaveSetting,
  apiGetImportHistory,
  apiSaveImportHistory,
  apiGetAllShippingCarriers,
  apiGetShippingCarrier,
  apiSaveShippingCarrier,
  apiUpdateShippingCarrier,
  apiDeleteShippingCarrier,
  apiGetAllShippingRoutes,
  apiSaveShippingRoute,
  apiUpdateShippingRoute,
  apiDeleteShippingRoute,
  apiGetAllCarrierRouteRates,
  apiGetCarrierRouteRates,
  apiUpsertCarrierRouteRates,
  apiDeleteCarrierRouteRates,
  apiGetRouteShippingConfig,
  apiGetAllRouteShippingConfigs,
  apiUpsertRouteShippingConfig,
} from '../services/api';
import { logger } from './logger';

// Legacy type aliases for backward compatibility
type CustomMarketplace = Marketplace;
type CustomMarketplaceProductData = MarketplaceProductData;

// Generate UUID
export const generateId = (): string => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
};

// Helper to convert snake_case to camelCase and parse dates from API response
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const parseDates = <T = any>(obj: Record<string, any>): T => {
  if (!obj || typeof obj !== 'object') return obj as T;
  const result = { ...obj };

  // Parse date fields
  const dateFields = ['createdAt', 'updatedAt', 'timestamp', 'periodStart', 'periodEnd', 'created_at', 'updated_at'];
  for (const field of dateFields) {
    if (result[field]) {
      result[field] = new Date(String(result[field]));
    }
  }

  // Map snake_case to camelCase for all common fields
  const snakeToCamelMap: Record<string, string> = {
    'created_at': 'createdAt',
    'updated_at': 'updatedAt',
    'base_cost': 'baseCost',
    'short_name': 'shortName',
    'is_active': 'isActive',
    'is_custom': 'isCustom',
    'is_default': 'isDefault',
    'fulfillment_options': 'fulfillmentOptions',
    'default_fulfillment': 'defaultFulfillment',
    'display_order': 'displayOrder',
    'country_code': 'countryCode',
    'marketplace_id': 'marketplaceId',
    'product_id': 'productId',
    'column_values': 'columnValues',
    'fulfillment_type': 'fulfillmentType',
    'target_margin': 'targetMargin',
    'column_defaults': 'columnDefaults',
    'sample_size': 'sampleSize',
    'data_source': 'dataSource',
    'period_start': 'periodStart',
    'period_end': 'periodEnd',
    'from_country': 'origin',
    'to_country': 'destination',
    'default_currency': 'defaultCurrency',
    'currency_symbol': 'currencySymbol',
    'route_id': 'routeId',
    'carrier_id': 'carrierId',
    'weight_brackets': 'weightBrackets',
    'default_carrier_id': 'defaultCarrierId',
    'preferred_carriers': 'preferredCarriers',
    'customs_config': 'customsConfig',
    'packaging_config': 'packagingConfig',
    'selection_mode': 'selectionMode',
    'priority_carrier_id': 'priorityCarrierId',
    'enabled_routes': 'enabledRoutes',
    'product_sku': 'productSku',
    'manual_size': 'manualSize',
    'desi_eff': 'desiEff',
  };

  for (const [snakeKey, camelKey] of Object.entries(snakeToCamelMap)) {
    if (snakeKey in result && !(camelKey in result)) {
      result[camelKey] = result[snakeKey];
      delete result[snakeKey];
    }
  }

  return result as T;
};

// ============================================
// PRODUCTS
// ============================================

export const saveProduct = async (product: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>): Promise<Product> => {
  const result = await apiSaveProduct(product);
  return parseDates(result);
};

export const updateProduct = async (product: Product): Promise<Product> => {
  const result = await apiUpdateProduct(product);
  return parseDates(result);
};

export const getProduct = async (id: string): Promise<Product | null> => {
  try {
    const result = await apiGetProduct(id);
    return result ? parseDates(result) : null;
  } catch {
    return null;
  }
};

export const getAllProducts = async (): Promise<Product[]> => {
  const results = await apiGetAllProducts();
  return results.map(parseDates);
};

export const deleteProduct = async (id: string): Promise<void> => {
  await apiDeleteProduct(id);
};

// ============================================
// SKU MAPPINGS
// ============================================

export const saveSKUMapping = async (mapping: Omit<SKUMapping, 'id' | 'createdAt' | 'updatedAt'>): Promise<SKUMapping> => {
  const result = await apiSaveSKUMapping(mapping);
  return parseDates(result);
};

export const getSKUMappingsByProduct = async (productId: string): Promise<SKUMapping[]> => {
  const results = await apiGetSKUMappingsByProduct(productId);
  return results.map(parseDates);
};

export const getSKUMappingsByPlatform = async (platform: MarketplacePlatform, marketplaceCode?: string): Promise<SKUMapping[]> => {
  const results = await apiGetSKUMappingsByPlatform(platform, marketplaceCode);
  return results.map(parseDates);
};

export const findSKUMapping = async (platform: MarketplacePlatform, sku: string): Promise<SKUMapping | null> => {
  try {
    const result = await apiFindSKUMapping(platform, sku);
    return result ? parseDates(result) : null;
  } catch {
    return null;
  }
};

// ============================================
// CATEGORY EXPENSES
// ============================================

export const saveCategoryExpense = async (expense: Omit<CategoryExpenseRates, 'id' | 'createdAt' | 'updatedAt'>): Promise<CategoryExpenseRates> => {
  const result = await apiUpsertCategoryExpense(expense);
  return parseDates(result);
};

export const getCategoryExpense = async (category: string, platform: MarketplacePlatform, marketplaceCode: string): Promise<CategoryExpenseRates | null> => {
  try {
    const result = await apiGetCategoryExpense(category, platform, marketplaceCode);
    return result ? parseDates(result) : null;
  } catch {
    return null;
  }
};

export const getAllCategoryExpenses = async (): Promise<CategoryExpenseRates[]> => {
  const results = await apiGetAllCategoryExpenses();
  return results.map(parseDates);
};

export const upsertCategoryExpense = async (expense: Omit<CategoryExpenseRates, 'id' | 'createdAt' | 'updatedAt'>): Promise<CategoryExpenseRates> => {
  const result = await apiUpsertCategoryExpense(expense);
  return parseDates(result);
};

export const getCategoryExpensesByPlatform = async (platform: MarketplacePlatform, marketplaceCode?: string): Promise<CategoryExpenseRates[]> => {
  const results = await apiGetCategoryExpensesByPlatform(platform, marketplaceCode);
  return results.map(parseDates);
};

// ============================================
// IMPORT HISTORY
// ============================================

export const saveImportHistory = async (history: Omit<ImportHistory, 'id'>): Promise<ImportHistory> => {
  const result = await apiSaveImportHistory(history);
  return parseDates(result);
};

export const getImportHistory = async (limit = 20): Promise<ImportHistory[]> => {
  const results = await apiGetImportHistory(limit);
  return results.map(parseDates);
};

// ============================================
// SETTINGS
// ============================================

export const saveSetting = async (key: string, value: unknown): Promise<void> => {
  await apiSaveSetting(key, value);
};

export const getSetting = async <T>(key: string): Promise<T | null> => {
  try {
    return await apiGetSetting<T>(key);
  } catch {
    return null;
  }
};

// ============================================
// DATABASE STATS
// ============================================

export const getDatabaseStats = async (): Promise<DatabaseStats> => {
  try {
    logger.debug('getDatabaseStats: Fetching stats...');
    // Use lightweight stats endpoint instead of fetching all products
    const stats = await apiGetProductStats();
    logger.debug('getDatabaseStats: Got stats:', stats);
    return {
      totalProducts: parseInt(stats.total_products, 10) || 0,
      totalSKUs: 0,
      skusByPlatform: { amazon: 0, trendyol: 0, shopify: 0, walmart: 0, wayfair: 0, bol: 0, takealot: 0, kaufland: 0 },
      categoriesCount: parseInt(stats.categories_count, 10) || 0,
      lastImport: stats.last_updated ? new Date(stats.last_updated) : null,
    };
  } catch (err) {
    logger.error('getDatabaseStats: Failed to load stats:', err);
    return {
      totalProducts: 0,
      totalSKUs: 0,
      skusByPlatform: { amazon: 0, trendyol: 0, shopify: 0, walmart: 0, wayfair: 0, bol: 0, takealot: 0, kaufland: 0 },
      categoriesCount: 0,
      lastImport: null,
    };
  }
};

// ============================================
// BULK OPERATIONS
// ============================================

export const bulkSaveProducts = async (products: Omit<Product, 'id' | 'createdAt' | 'updatedAt'>[]): Promise<{ added: number; updated: number }> => {
  // Convert camelCase to snake_case for API
  const apiProducts = products.map(p => ({
    name: p.name,
    category: p.category,
    base_cost: p.baseCost,
    size: p.size,
    weight: p.weight,
    width: p.width,
    height: p.height,
    length: p.length,
    source: p.source,
    product_sku: p.productSku,
  }));
  return await apiBulkSaveProducts(apiProducts);
};


// ============================================
// CUSTOM MARKETPLACES
// ============================================

export const saveCustomMarketplace = async (marketplace: Omit<CustomMarketplace, 'id' | 'createdAt' | 'updatedAt'>): Promise<CustomMarketplace> => {
  const result = await apiSaveMarketplace(marketplace);
  return parseDates(result);
};

export const updateCustomMarketplace = async (marketplace: CustomMarketplace): Promise<CustomMarketplace> => {
  const result = await apiUpdateMarketplace(marketplace);
  return parseDates(result);
};

export const getCustomMarketplace = async (id: string): Promise<CustomMarketplace | null> => {
  try {
    const result = await apiGetMarketplace(id);
    return result ? parseDates(result) : null;
  } catch {
    return null;
  }
};

export const getAllCustomMarketplaces = async (): Promise<CustomMarketplace[]> => {
  const results = await apiGetAllMarketplaces();
  return results.map(parseDates);
};

export const getActiveCustomMarketplaces = async (): Promise<CustomMarketplace[]> => {
  const all = await getAllCustomMarketplaces();
  return all.filter(m => m.isActive !== false);
};

export const deleteCustomMarketplace = async (id: string): Promise<void> => {
  await apiDeleteMarketplace(id);
};

// ============================================
// MARKETPLACE PRODUCT DATA
// ============================================

export const saveMarketplaceProductData = async (data: Omit<CustomMarketplaceProductData, 'id' | 'createdAt' | 'updatedAt'>): Promise<CustomMarketplaceProductData> => {
  const result = await apiUpsertMarketplaceProductData(data);
  return parseDates(result);
};

export const upsertMarketplaceProductData = async (data: Omit<CustomMarketplaceProductData, 'id' | 'createdAt' | 'updatedAt'>): Promise<CustomMarketplaceProductData> => {
  const result = await apiUpsertMarketplaceProductData(data);
  return parseDates(result);
};

export const getMarketplaceProductData = async (productId: string, marketplaceId: string, countryCode: string): Promise<CustomMarketplaceProductData | null> => {
  try {
    const result = await apiGetMarketplaceProductData(productId, marketplaceId, countryCode);
    return result ? parseDates(result) : null;
  } catch {
    return null;
  }
};

export const getProductDataByMarketplace = async (marketplaceId: string, countryCode?: string): Promise<CustomMarketplaceProductData[]> => {
  const results = await apiGetProductDataByMarketplace(marketplaceId, countryCode);
  return results.map(parseDates);
};

export const bulkSaveMarketplaceProductData = async (dataList: Omit<CustomMarketplaceProductData, 'id' | 'createdAt' | 'updatedAt'>[]): Promise<{ added: number; updated: number }> => {
  return await apiBulkSaveMarketplaceProductData(dataList);
};

// ============================================
// CATEGORY COLUMN DEFAULTS
// ============================================

export const saveCategoryColumnDefaults = async (defaults: Omit<CategoryColumnDefaults, 'id' | 'createdAt' | 'updatedAt'>): Promise<CategoryColumnDefaults> => {
  const result = await apiUpsertCategoryColumnDefaults(defaults);
  return parseDates(result);
};

export const upsertCategoryColumnDefaults = async (defaults: Omit<CategoryColumnDefaults, 'id' | 'createdAt' | 'updatedAt'>): Promise<CategoryColumnDefaults> => {
  const result = await apiUpsertCategoryColumnDefaults(defaults);
  return parseDates(result);
};

export const getCategoryColumnDefaults = async (marketplaceId: string, countryCode: string, category: string, fulfillmentType?: string): Promise<CategoryColumnDefaults | null> => {
  try {
    const result = await apiGetCategoryColumnDefaults(marketplaceId, countryCode, category, fulfillmentType);
    return result ? parseDates(result) : null;
  } catch {
    return null;
  }
};

export const getCategoryDefaultsByMarketplace = async (marketplaceId: string, countryCode?: string): Promise<CategoryColumnDefaults[]> => {
  const results = await apiGetCategoryDefaultsByMarketplace(marketplaceId, countryCode);
  return results.map(parseDates);
};

export const deleteCategoryColumnDefaults = async (id: string): Promise<void> => {
  await apiDeleteCategoryColumnDefaults(id);
};

export const deleteCategoryDefaultsByCountry = async (marketplaceId: string, countryCode: string): Promise<void> => {
  await apiDeleteCategoryDefaultsByCountry(marketplaceId, countryCode);
};

/**
 * Clear all category column defaults for a marketplace (all countries)
 * Used before bulk import to prevent data pollution
 */
export const clearMarketplaceCategoryDefaults = async (marketplaceId: string): Promise<number> => {
  try {
    const result = await apiDeleteCategoryDefaultsByMarketplace(marketplaceId);
    logger.debug(`Cleared ${result.deleted} category defaults for marketplace ${marketplaceId}`);
    return result.deleted;
  } catch (error) {
    logger.error('clearMarketplaceCategoryDefaults: Failed:', error);
    throw error;
  }
};

export const bulkUpsertCategoryColumnDefaults = async (defaultsList: Omit<CategoryColumnDefaults, 'id' | 'createdAt' | 'updatedAt'>[]): Promise<{ imported: number; updated: number }> => {
  return await apiBulkUpsertCategoryColumnDefaults(defaultsList);
};

export const getAllCategoryColumnDefaults = async (): Promise<CategoryColumnDefaults[]> => {
  const results = await apiGetAllCategoryColumnDefaults();
  return results.map(parseDates);
};

// ============================================
// DEFAULT MARKETPLACE DATA
// ============================================

export const getDefaultAmazonColumns = (): MarketplaceColumn[] => [
  // Platform Fees - category-based (varies by category)
  { id: 'col_selling', name: 'Selling', key: 'sellingPercent', type: 'percent', category: 'platform', scope: 'category', visible: true, editable: true, defaultValue: 15, order: 1, description: 'Amazon referral/commission fee' },
  { id: 'col_fba_fee', name: 'FBA Fee', key: 'fbaFeePercent', type: 'percent', category: 'platform', scope: 'category', visible: true, editable: true, defaultValue: 12, order: 2, description: 'Fulfillment by Amazon fee' },
  { id: 'col_refund', name: 'Refund', key: 'refundPercent', type: 'percent', category: 'platform', scope: 'category', visible: true, editable: true, defaultValue: 3, order: 3, description: 'Expected refund/return rate' },
  { id: 'col_vat', name: 'VAT', key: 'vatPercent', type: 'percent', category: 'platform', scope: 'country', visible: true, editable: true, defaultValue: 0, order: 4, description: 'Value Added Tax' },
  // General Costs - category-based
  { id: 'col_ads', name: 'Ads', key: 'adsPercent', type: 'percent', category: 'general', scope: 'category', visible: true, editable: true, defaultValue: 8, order: 5, description: 'PPC/Advertising spend' },
  { id: 'col_fba_cost', name: 'FBA Cost', key: 'fbaCostPercent', type: 'percent', category: 'general', scope: 'category', visible: true, editable: true, defaultValue: 2, order: 6, description: 'FBA storage and inventory costs' },
  { id: 'col_fbm_cost', name: 'FBM Cost', key: 'fbmCostPercent', type: 'percent', category: 'general', scope: 'category', visible: false, editable: true, defaultValue: 0, order: 7, description: 'Merchant fulfillment costs (exc. shipping)' },
  // Logistics - country-based (same for all categories in a country)
  { id: 'col_ship', name: 'Ship', key: 'shipPercent', type: 'percent', category: 'logistics', scope: 'country', visible: true, editable: true, defaultValue: 5, order: 8, description: 'Inbound shipping cost (FBA only)' },
  { id: 'col_customs', name: 'Customs', key: 'customsPercent', type: 'percent', category: 'logistics', scope: 'country', visible: false, editable: true, defaultValue: 0, order: 9, description: 'Import duties' },
  { id: 'col_ddp', name: 'DDP', key: 'ddpPercent', type: 'percent', category: 'logistics', scope: 'country', visible: false, editable: true, defaultValue: 0, order: 10, description: 'Delivered Duty Paid' },
  { id: 'col_warehouse', name: 'Warehouse', key: 'warehousePercent', type: 'percent', category: 'logistics', scope: 'country', visible: false, editable: true, defaultValue: 0, order: 11, description: '3PL warehouse costs' },
  { id: 'col_gst', name: 'GST', key: 'gstPercent', type: 'percent', category: 'logistics', scope: 'country', visible: false, editable: true, defaultValue: 0, order: 12, description: 'Goods and Services Tax (AU/SG)' },
];

export const getDefaultAmazonCountries = (): MarketplaceCountry[] => [
  // North America
  { code: 'US', name: 'United States', currency: 'USD', currencySymbol: '$', taxRate: 0, taxName: 'Sales Tax', flag: '🇺🇸', isActive: true,
    fulfillmentOptions: [
      { code: 'FBA', name: 'FBA', description: 'Fulfillment by Amazon', isDefault: true },
      { code: 'FBM-US', name: 'FBM (US)', description: 'Fulfilled by Merchant from US' },
      { code: 'FBM-TR', name: 'FBM (TR)', description: 'Fulfilled by Merchant from Turkey' },
    ]
  },
  { code: 'CA', name: 'Canada', currency: 'CAD', currencySymbol: 'C$', taxRate: 5, taxName: 'GST', flag: '🇨🇦', isActive: true },
  { code: 'MX', name: 'Mexico', currency: 'MXN', currencySymbol: 'MX$', taxRate: 16, taxName: 'IVA', flag: '🇲🇽', isActive: true },
  { code: 'BR', name: 'Brazil', currency: 'BRL', currencySymbol: 'R$', taxRate: 17, taxName: 'ICMS', flag: '🇧🇷', isActive: true },
  // Europe
  { code: 'UK', name: 'United Kingdom', currency: 'GBP', currencySymbol: '£', taxRate: 20, taxName: 'VAT', flag: '🇬🇧', isActive: true },
  { code: 'DE', name: 'Germany', currency: 'EUR', currencySymbol: '€', taxRate: 19, taxName: 'VAT', flag: '🇩🇪', isActive: true },
  { code: 'FR', name: 'France', currency: 'EUR', currencySymbol: '€', taxRate: 20, taxName: 'VAT', flag: '🇫🇷', isActive: true },
  { code: 'IT', name: 'Italy', currency: 'EUR', currencySymbol: '€', taxRate: 22, taxName: 'VAT', flag: '🇮🇹', isActive: true },
  { code: 'ES', name: 'Spain', currency: 'EUR', currencySymbol: '€', taxRate: 21, taxName: 'VAT', flag: '🇪🇸', isActive: true },
  { code: 'NL', name: 'Netherlands', currency: 'EUR', currencySymbol: '€', taxRate: 21, taxName: 'VAT', flag: '🇳🇱', isActive: true },
  { code: 'PL', name: 'Poland', currency: 'PLN', currencySymbol: 'zł', taxRate: 23, taxName: 'VAT', flag: '🇵🇱', isActive: true },
  { code: 'SE', name: 'Sweden', currency: 'SEK', currencySymbol: 'kr', taxRate: 25, taxName: 'VAT', flag: '🇸🇪', isActive: true },
  { code: 'BE', name: 'Belgium', currency: 'EUR', currencySymbol: '€', taxRate: 21, taxName: 'VAT', flag: '🇧🇪', isActive: true },
  { code: 'TR', name: 'Turkey', currency: 'TRY', currencySymbol: '₺', taxRate: 20, taxName: 'KDV', flag: '🇹🇷', isActive: true },
  // Asia Pacific
  { code: 'JP', name: 'Japan', currency: 'JPY', currencySymbol: '¥', taxRate: 10, taxName: 'JCT', flag: '🇯🇵', isActive: true },
  { code: 'AU', name: 'Australia', currency: 'AUD', currencySymbol: 'A$', taxRate: 10, taxName: 'GST', flag: '🇦🇺', isActive: true },
  { code: 'SG', name: 'Singapore', currency: 'SGD', currencySymbol: 'S$', taxRate: 9, taxName: 'GST', flag: '🇸🇬', isActive: true },
  { code: 'IN', name: 'India', currency: 'INR', currencySymbol: '₹', taxRate: 18, taxName: 'GST', flag: '🇮🇳', isActive: true },
  // Middle East
  { code: 'AE', name: 'UAE', currency: 'AED', currencySymbol: 'د.إ', taxRate: 5, taxName: 'VAT', flag: '🇦🇪', isActive: true },
  { code: 'SA', name: 'Saudi Arabia', currency: 'SAR', currencySymbol: '﷼', taxRate: 15, taxName: 'VAT', flag: '🇸🇦', isActive: true },
  { code: 'EG', name: 'Egypt', currency: 'EGP', currencySymbol: 'E£', taxRate: 14, taxName: 'VAT', flag: '🇪🇬', isActive: true },
];

export const createDefaultAmazonMarketplace = (): Omit<Marketplace, 'id' | 'createdAt' | 'updatedAt'> => ({
  name: 'Amazon',
  shortName: 'AMZ',
  icon: '📦',
  color: '#FF9900',
  countries: getDefaultAmazonCountries(),
  columns: getDefaultAmazonColumns(),
  fulfillmentOptions: ['FBA', 'FBM'],
  defaultFulfillment: 'FBA',
  isActive: true,
  displayOrder: 1,
});

export const createDefaultTrendyolMarketplace = (): Omit<Marketplace, 'id' | 'createdAt' | 'updatedAt'> => ({
  name: 'Trendyol',
  shortName: 'TY',
  icon: '🛒',
  color: '#F27A1A',
  countries: [
    { code: 'TR', name: 'Turkey', currency: 'TRY', currencySymbol: '₺', taxRate: 20, taxName: 'KDV', flag: '🇹🇷', isActive: true },
  ],
  columns: [
    { id: 'col_commission', name: 'Commission', key: 'commissionPercent', type: 'percent', category: 'platform', scope: 'category', visible: true, editable: true, defaultValue: 12, order: 1, description: 'Trendyol commission' },
    { id: 'col_service_fee', name: 'Service Fee', key: 'serviceFeePercent', type: 'percent', category: 'platform', scope: 'category', visible: true, editable: true, defaultValue: 3, order: 2, description: 'Platform service fee' },
    { id: 'col_kdv', name: 'KDV', key: 'kdvPercent', type: 'percent', category: 'platform', scope: 'country', visible: true, editable: true, defaultValue: 20, order: 3, description: 'Value Added Tax' },
    { id: 'col_ads', name: 'Reklam', key: 'adsPercent', type: 'percent', category: 'general', scope: 'category', visible: true, editable: true, defaultValue: 5, order: 4, description: 'Advertising cost' },
    { id: 'col_cargo', name: 'Kargo', key: 'cargoPercent', type: 'percent', category: 'logistics', scope: 'country', visible: true, editable: true, defaultValue: 8, order: 5, description: 'Shipping cost' },
  ],
  fulfillmentOptions: ['Marketplace', 'Seller'],
  defaultFulfillment: 'Marketplace',
  isActive: true,
  displayOrder: 2,
});

export const initializeDefaultMarketplaces = async (_forceUpdate: boolean = false): Promise<void> => {
  try {
    const existing = await getAllCustomMarketplaces();
    logger.debug('initializeDefaultMarketplaces: Found', existing.length, 'marketplaces');

    // If marketplaces already exist, don't create new ones
    if (existing.length > 0) {
      logger.debug('initializeDefaultMarketplaces: Marketplaces exist, skipping creation');
      return;
    }

    // Only create if no marketplaces exist
    logger.debug('initializeDefaultMarketplaces: No marketplaces found, creating Amazon');
    await saveCustomMarketplace(createDefaultAmazonMarketplace());
  } catch (error) {
    logger.error('initializeDefaultMarketplaces: Error:', error);
    // Don't throw - let the app continue even if this fails
  }
};

export const resetMarketplacesToDefault = async (): Promise<void> => {
  const existing = await getAllCustomMarketplaces();
  for (const m of existing) {
    await deleteCustomMarketplace(m.id);
  }
  await saveCustomMarketplace(createDefaultAmazonMarketplace());
};

// ============================================
// DATABASE BACKUP/RESTORE
// ============================================

interface DatabaseBackup {
  version: number;
  exportedAt: string;
  data: {
    products: Product[];
    skuMappings: SKUMapping[];
    categoryExpenses: CategoryExpenseRates[];
    importHistory: ImportHistory[];
    settings: Record<string, unknown>[];
    marketplaces: Marketplace[];
    marketplaceProductData: MarketplaceProductData[];
    categoryColumnDefaults: CategoryColumnDefaults[];
    shippingCarriers: ShippingCarrier[];
    carrierRouteRates: CarrierRouteRates[];
    routeShippingConfigs: RouteShippingConfig[];
    customShippingRoutes: CustomShippingRoute[];
  };
}

export const exportDatabaseToJSON = async (): Promise<DatabaseBackup> => {
  const [
    products,
    categoryExpenses,
    marketplaces,
    categoryColumnDefaults,
    shippingCarriers,
    carrierRouteRates,
    routeShippingConfigs,
    customShippingRoutes,
  ] = await Promise.all([
    getAllProducts(),
    getAllCategoryExpenses(),
    getAllCustomMarketplaces(),
    getAllCategoryColumnDefaults(),
    getAllShippingCarriers(),
    getAllCarrierRouteRates(),
    getAllRouteShippingConfigs(),
    getAllCustomShippingRoutes(),
  ]);

  return {
    version: 8,
    exportedAt: new Date().toISOString(),
    data: {
      products,
      skuMappings: [],
      categoryExpenses,
      importHistory: [],
      settings: [],
      marketplaces,
      marketplaceProductData: [],
      categoryColumnDefaults,
      shippingCarriers,
      carrierRouteRates,
      routeShippingConfigs,
      customShippingRoutes,
    },
  };
};

export const importDatabaseFromJSON = async (backup: DatabaseBackup, _clearExisting: boolean = true): Promise<{
  success: boolean;
  imported: { [key: string]: number };
  errors: string[];
}> => {
  const imported: { [key: string]: number } = {};
  const errors: string[] = [];

  try {
    // Import products
    if (backup.data.products) {
      for (const product of backup.data.products) {
        try {
          await saveProduct(product);
          imported.products = (imported.products || 0) + 1;
        } catch (e: unknown) {
          errors.push(`Product: ${e instanceof Error ? e.message : String(e)}`);
        }
      }
    }

    // Import marketplaces
    if (backup.data.marketplaces) {
      for (const marketplace of backup.data.marketplaces) {
        try {
          await saveCustomMarketplace(marketplace);
          imported.marketplaces = (imported.marketplaces || 0) + 1;
        } catch (e: unknown) {
          errors.push(`Marketplace: ${e instanceof Error ? e.message : String(e)}`);
        }
      }
    }

    return { success: true, imported, errors };
  } catch (e: unknown) {
    return { success: false, imported, errors: [e instanceof Error ? e.message : String(e)] };
  }
};

export const saveAutoBackup = async (): Promise<void> => {
  // Auto backup is handled by the server now
  logger.debug('Auto backup: Handled by server');
};

export const loadAutoBackup = async (): Promise<boolean> => {
  // No longer needed with server-side database
  return false;
};

export const getLastBackupTime = (): Date | null => {
  const timeStr = localStorage.getItem('lastAutoBackup');
  return timeStr ? new Date(timeStr) : null;
};

export const downloadBackupFile = async (): Promise<void> => {
  const backup = await exportDatabaseToJSON();
  const json = JSON.stringify(backup, null, 2);
  const blob = new Blob([json], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `pricelab-backup-${new Date().toISOString().split('T')[0]}.json`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
};

export const importBackupFile = async (file: File): Promise<{
  success: boolean;
  imported: { [key: string]: number };
  errors: string[];
}> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = async (e) => {
      try {
        const backup = JSON.parse(e.target?.result as string);
        const result = await importDatabaseFromJSON(backup, true);
        resolve(result);
      } catch (error: unknown) {
        reject(error);
      }
    };
    reader.onerror = () => reject(reader.error);
    reader.readAsText(file);
  });
};

// ============================================
// SHIPPING CARRIERS
// ============================================

export const saveShippingCarrier = async (carrier: Omit<ShippingCarrier, 'id' | 'createdAt' | 'updatedAt'>): Promise<ShippingCarrier> => {
  const result = await apiSaveShippingCarrier(carrier);
  return parseDates(result);
};

export const updateShippingCarrier = async (carrier: ShippingCarrier): Promise<ShippingCarrier> => {
  const result = await apiUpdateShippingCarrier(carrier);
  return parseDates(result);
};

export const getShippingCarrier = async (id: string): Promise<ShippingCarrier | null> => {
  try {
    const result = await apiGetShippingCarrier(id);
    return result ? parseDates(result) : null;
  } catch {
    return null;
  }
};

export const getShippingCarrierByShortName = async (shortName: string): Promise<ShippingCarrier | null> => {
  const carriers = await getAllShippingCarriers();
  return carriers.find(c => c.shortName === shortName) || null;
};

export const getAllShippingCarriers = async (): Promise<ShippingCarrier[]> => {
  const results = await apiGetAllShippingCarriers();
  return results.map(parseDates);
};

export const getActiveShippingCarriers = async (): Promise<ShippingCarrier[]> => {
  const all = await getAllShippingCarriers();
  return all.filter(c => c.isActive !== false);
};

export const deleteShippingCarrier = async (id: string): Promise<void> => {
  await apiDeleteShippingCarrier(id);
};

// ============================================
// CARRIER ROUTE RATES
// ============================================

export const saveCarrierRouteRates = async (rates: Omit<CarrierRouteRates, 'id' | 'createdAt' | 'updatedAt'>): Promise<CarrierRouteRates> => {
  const result = await apiUpsertCarrierRouteRates(rates);
  return parseDates(result);
};

export const updateCarrierRouteRates = async (rates: CarrierRouteRates): Promise<CarrierRouteRates> => {
  const result = await apiUpsertCarrierRouteRates(rates);
  return parseDates(result);
};

export const getCarrierRouteRates = async (carrierId: string, routeId: string): Promise<CarrierRouteRates | null> => {
  try {
    const result = await apiGetCarrierRouteRates(carrierId, routeId);
    return result ? parseDates(result) : null;
  } catch {
    return null;
  }
};

export const getCarrierAllRouteRates = async (carrierId: string): Promise<CarrierRouteRates[]> => {
  const all = await getAllCarrierRouteRates();
  return all.filter(r => r.carrierId === carrierId);
};

export const getRouteAllCarrierRates = async (routeId: string): Promise<CarrierRouteRates[]> => {
  const all = await getAllCarrierRouteRates();
  return all.filter(r => r.routeId === routeId);
};

export const getAllCarrierRouteRates = async (): Promise<CarrierRouteRates[]> => {
  const results = await apiGetAllCarrierRouteRates();
  return results.map(r => {
    const parsed = parseDates<CarrierRouteRates>(r);
    // Use route_key (from_country_to_country) as routeId for frontend matching
    // Backend now returns route_key from JOIN with shipping_routes
    const routeId = ((r.route_key as string) || parsed.routeId) as ShippingRouteId;
    // Convert weight_brackets to rates format
    const rates = (r.weight_brackets as Record<number, number>) || parsed.rates || {};
    return {
      ...parsed,
      routeId,
      rates,
      carrierId: (r.carrier_id as string) || parsed.carrierId,
    };
  });
};

export const upsertCarrierRouteRates = async (rates: Omit<CarrierRouteRates, 'id' | 'createdAt' | 'updatedAt'>): Promise<CarrierRouteRates> => {
  const result = await apiUpsertCarrierRouteRates(rates);
  return parseDates(result);
};

export const deleteCarrierRouteRates = async (id: string): Promise<void> => {
  await apiDeleteCarrierRouteRates(id);
};

// ============================================
// ROUTE SHIPPING CONFIG
// ============================================

export const saveRouteShippingConfig = async (config: Omit<RouteShippingConfig, 'id' | 'createdAt' | 'updatedAt'>): Promise<RouteShippingConfig> => {
  const result = await apiUpsertRouteShippingConfig(config);
  return parseDates(result);
};

export const updateRouteShippingConfig = async (config: RouteShippingConfig): Promise<RouteShippingConfig> => {
  const result = await apiUpsertRouteShippingConfig(config);
  return parseDates(result);
};

export const getRouteShippingConfig = async (routeId: string): Promise<RouteShippingConfig | null> => {
  try {
    const result = await apiGetRouteShippingConfig(routeId);
    return result ? parseDates(result) : null;
  } catch {
    return null;
  }
};

export const getAllRouteShippingConfigs = async (): Promise<RouteShippingConfig[]> => {
  const results = await apiGetAllRouteShippingConfigs();
  return results.map(parseDates);
};

export const upsertRouteShippingConfig = async (config: Omit<RouteShippingConfig, 'id' | 'createdAt' | 'updatedAt'>): Promise<RouteShippingConfig> => {
  const result = await apiUpsertRouteShippingConfig(config);
  return parseDates(result);
};

export const initializeDefaultShippingCarriers = async (): Promise<void> => {
  const existing = await getAllShippingCarriers();
  if (existing.length === 0) {
    for (const carrier of DEFAULT_CARRIERS) {
      await saveShippingCarrier(carrier);
    }
  }
};

// ============================================
// CUSTOM SHIPPING ROUTES
// ============================================

export const saveCustomShippingRoute = async (route: Omit<CustomShippingRoute, 'id' | 'createdAt' | 'updatedAt'>): Promise<CustomShippingRoute> => {
  const result = await apiSaveShippingRoute(route);
  return parseDates(result);
};

export const updateCustomShippingRoute = async (route: CustomShippingRoute): Promise<CustomShippingRoute> => {
  const result = await apiUpdateShippingRoute(route);
  return parseDates(result);
};

export const getCustomShippingRoute = async (id: string): Promise<CustomShippingRoute | null> => {
  const routes = await getAllCustomShippingRoutes();
  return routes.find(r => r.id === id) || null;
};

export const getCustomShippingRouteByRouteId = async (routeId: string): Promise<CustomShippingRoute | null> => {
  const routes = await getAllCustomShippingRoutes();
  return routes.find(r => r.routeId === routeId) || null;
};

// Currency mapping for routes based on your Excel
const getRouteCurrency = (routeName: string): { currency: string; symbol: string } => {
  const currencies: Record<string, { currency: string; symbol: string }> = {
    'US-US': { currency: 'USD', symbol: '$' },
    'US-TR': { currency: 'USD', symbol: '$' },
    'UK': { currency: 'EUR', symbol: '€' },
    'CA': { currency: 'USD', symbol: '$' },
    'EU': { currency: 'EUR', symbol: '€' },
    'AU': { currency: 'EUR', symbol: '€' },
    'UAE': { currency: 'USD', symbol: '$' },
    'TR': { currency: 'TRY', symbol: '₺' },
    'SG': { currency: 'USD', symbol: '$' },
    'SA': { currency: 'USD', symbol: '$' },
  };
  return currencies[routeName] || { currency: 'USD', symbol: '$' };
};

export const getAllCustomShippingRoutes = async (): Promise<CustomShippingRoute[]> => {
  const results = await apiGetAllShippingRoutes();
  return results.map(r => {
    const parsed = parseDates<CustomShippingRoute & Record<string, unknown>>(r);
    const fromCountry = parsed.origin || (parsed.from_country as string) || 'TR';
    const toCountry = parsed.destination || (parsed.to_country as string) || '';
    // routeId must be in "FROM_TO" format (e.g., "TR_US", "TR_UK") for database matching
    const routeId = `${fromCountry}_${toCountry}`;
    // shortName: use route name (US-US, UK, CA, etc.) or destination for display
    const shortName = parsed.name || parsed.shortName || toCountry || '';
    // flag: generate from destination country
    const flag = parsed.flag || getCountryFlag(toCountry);
    // currency from route name
    const { currency: defaultCurrency, symbol: currencySymbol } = getRouteCurrency(parsed.name);

    return {
      ...parsed,
      routeId,
      shortName,
      origin: fromCountry,
      destination: toCountry,
      flag,
      defaultCurrency,
      currencySymbol,
    };
  });
};

// Helper to get flag emoji from country code
const getCountryFlag = (countryCode: string): string => {
  const flags: Record<string, string> = {
    'US': '🇺🇸', 'UK': '🇬🇧', 'DE': '🇩🇪', 'FR': '🇫🇷', 'IT': '🇮🇹', 'ES': '🇪🇸',
    'NL': '🇳🇱', 'BE': '🇧🇪', 'AT': '🇦🇹', 'PL': '🇵🇱', 'SE': '🇸🇪', 'DK': '🇩🇰',
    'TR': '🇹🇷', 'CN': '🇨🇳', 'JP': '🇯🇵', 'AU': '🇦🇺', 'CA': '🇨🇦', 'MX': '🇲🇽',
    'BR': '🇧🇷', 'EU': '🇪🇺', 'AE': '🇦🇪', 'SA': '🇸🇦', 'IN': '🇮🇳', 'SG': '🇸🇬',
  };
  return flags[countryCode?.toUpperCase()] || '🌍';
};

export const getActiveCustomShippingRoutes = async (): Promise<CustomShippingRoute[]> => {
  const all = await getAllCustomShippingRoutes();
  return all.filter(r => r.isActive !== false);
};

export const deleteCustomShippingRoute = async (id: string): Promise<void> => {
  await apiDeleteShippingRoute(id);
};

export const initializeDefaultShippingRoutes = async (): Promise<void> => {
  const existing = await getAllCustomShippingRoutes();
  if (existing.length === 0) {
    for (const route of SHIPPING_ROUTES) {
      await saveCustomShippingRoute({
        routeId: route.id,
        name: route.name,
        shortName: route.shortName,
        origin: route.origin,
        destination: route.destination,
        flag: route.flag,
        defaultCurrency: route.defaultCurrency,
        currencySymbol: route.currencySymbol,
        isActive: true,
        isDefault: true,
      });
    }
  }
};

